import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { DEFAULT_COMPANY_TIMEZONE, getCompanyTimezone } from '@/lib/company-timezone'
import { humanizeFieldError } from '@/lib/api-messages'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatPhoneNumber(phone: string): string {
  if (!phone) return ''
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 12 && cleaned.startsWith('91')) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`
  }
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`
  }
  return phone
}

/**
 * Contact-identifier display chain (WhatsApp usernames rollout, 2026):
 * `@username` → formatted phone → shortened BSUID. Username-only contacts
 * have NO phone (Meta may withhold it), so every contact-identity render
 * must go through this instead of reading `phone_number` directly.
 */
export function formatContactIdentifier(contact: {
  username?: string | null
  phone_number?: string | null
  bsuid?: string | null
}): string {
  if (contact.username) return `@${contact.username}`
  if (contact.phone_number) return formatPhoneNumber(contact.phone_number)
  if (contact.bsuid) return shortenBsuid(contact.bsuid)
  return ''
}

/** "IN.13491208655302741918" → "IN.…41918" (readable, still unique-ish). */
export function shortenBsuid(bsuid: string): string {
  if (!bsuid) return ''
  const dot = bsuid.indexOf('.')
  if (dot === -1 || bsuid.length <= 12) return bsuid
  return `${bsuid.slice(0, dot + 1)}…${bsuid.slice(-5)}`
}

/** True when the contact can only be reached via BSUID (no phone shared). */
export function isPhonelessContact(contact: {
  phone_number?: string | null
  bsuid?: string | null
}): boolean {
  return !contact.phone_number && !!contact.bsuid
}

export type DateTimeParts = {
  primary: string
  meridiem: string
}

function resolveTimezone(timezone?: string): string {
  return timezone || getCompanyTimezone() || DEFAULT_COMPANY_TIMEZONE
}

function calendarDateKey(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/** e.g. { primary: "Apr 21, 2026 • 05:25", meridiem: "PM" } */
export function formatDateTimeParts(
  date: string | Date,
  timezone?: string,
): DateTimeParts | null {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return null

  const tz = resolveTimezone(timezone)

  const datePart = d.toLocaleDateString('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d)

  const hour = parts.find((p) => p.type === 'hour')?.value ?? ''
  const minute = parts.find((p) => p.type === 'minute')?.value ?? ''
  const meridiem = parts.find((p) => p.type === 'dayPeriod')?.value?.toUpperCase() ?? ''

  return {
    primary: `${datePart} • ${hour}:${minute}`,
    meridiem,
  }
}

/** Single-line datetime, e.g. "Apr 21, 2026 • 05:25 PM" */
export function formatDateTime(date: string | Date, timezone?: string): string {
  const parts = formatDateTimeParts(date, timezone)
  if (!parts) return ''
  return parts.meridiem ? `${parts.primary} ${parts.meridiem}` : parts.primary
}

export function formatDate(
  date: string | Date,
  format: 'relative' | 'short' | 'long' = 'short',
  timezone?: string,
): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''

  const tz = resolveTimezone(timezone)

  if (format === 'relative') {
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMin < 1) return 'just now'
    if (diffMin < 60) return `${diffMin}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return d.toLocaleDateString('en-US', {
      timeZone: tz,
      month: 'short',
      day: 'numeric',
    })
  }

  return formatDateTime(d, tz)
}

/**
 * Format a timestamp for chat bubbles: "2:30 PM"
 * Shows time only for today, "Jan 12, 2:30 PM" for older.
 */
export function formatMessageTime(date: string | Date, timezone?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''

  const tz = resolveTimezone(timezone)
  const now = new Date()
  const dateKey = calendarDateKey(d, tz)
  const todayKey = calendarDateKey(now, tz)

  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayKey = calendarDateKey(yesterday, tz)

  const time = d.toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  if (dateKey === todayKey) return time
  if (dateKey === yesterdayKey) return `Yesterday, ${time}`
  return (
    d.toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' }) + `, ${time}`
  )
}

/** Time of day only, e.g. "2:59 PM", in the company's timezone. */
export function formatClockTime(date: string | Date, timezone?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('en-US', {
    timeZone: resolveTimezone(timezone),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Format a date for chat day separators: "Today", "Yesterday", "Jan 12, 2026"
 */
export function formatChatDate(date: string | Date, timezone?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''

  const tz = resolveTimezone(timezone)
  const now = new Date()
  const dateKey = calendarDateKey(d, tz)
  const todayKey = calendarDateKey(now, tz)

  if (dateKey === todayKey) return 'Today'

  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (dateKey === calendarDateKey(yesterday, tz)) return 'Yesterday'

  return d.toLocaleDateString('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Convert a naive datetime-local value (wall clock in `timeZone`) to UTC ISO string.
 * Example: "2026-04-14T19:20" + "Asia/Kolkata" → "2026-04-14T13:50:00.000Z" (7:20 PM IST).
 */
export function localToUTC(naiveDatetime: string, timeZone: string): string {
  if (!naiveDatetime) return ''

  const [datePart, rawTime = '00:00:00'] = naiveDatetime.split('T')
  const [yStr, mStr, dStr] = datePart.split('-')
  const Y = Number(yStr)
  const M = Number(mStr)
  const D = Number(dStr)
  const timeBits = rawTime.split(':')
  const hh = Number(timeBits[0] ?? 0)
  const mi = Number(timeBits[1] ?? 0)
  const sec = Number(timeBits[2] ?? 0)

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })

  const read = (ms: number) => {
    const p: Record<string, number> = {}
    for (const part of formatter.formatToParts(new Date(ms))) {
      if (part.type === 'year') p.year = Number(part.value)
      else if (part.type === 'month') p.month = Number(part.value)
      else if (part.type === 'day') p.day = Number(part.value)
      else if (part.type === 'hour') p.hour = Number(part.value)
      else if (part.type === 'minute') p.minute = Number(part.value)
      else if (part.type === 'second') p.second = Number(part.value)
    }
    return p
  }

  const targetKey =
    Y * 1e10 +
    M * 1e8 +
    D * 1e6 +
    hh * 1e4 +
    mi * 100 +
    sec

  const keyAt = (ms: number) => {
    const p = read(ms)
    return (
      p.year * 1e10 +
      p.month * 1e8 +
      p.day * 1e6 +
      p.hour * 1e4 +
      p.minute * 100 +
      p.second
    )
  }

  const anchor = Date.UTC(Y, M - 1, D, hh, mi, sec)
  let lo = anchor - 72 * 3600000
  let hi = anchor + 72 * 3600000

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2)
    const k = keyAt(mid)
    if (k === targetKey) return new Date(mid).toISOString()
    if (k < targetKey) lo = mid + 1
    else hi = mid - 1
  }

  for (let t = anchor - 72 * 3600000; t <= anchor + 72 * 3600000; t += 60000) {
    if (keyAt(t) === targetKey) return new Date(t).toISOString()
  }

  return new Date(anchor).toISOString()
}

/**
 * Format a UTC instant as `YYYY-MM-DDTHH:mm` for datetime-local, using wall clock in `timeZone`.
 */
export function utcIsoToDatetimeLocalInTimeZone(isoUtc: string | Date, timeZone: string): string {
  const d = typeof isoUtc === 'string' ? new Date(isoUtc) : isoUtc
  if (isNaN(d.getTime())) return ''

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  const parts = formatter.formatToParts(d)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}

export function formatInTimezone(
  utcDatetime: string | Date,
  timezone: string,
  format: 'short' | 'long' | 'relative' = 'short',
): string {
  if (format === 'relative') {
    return formatDate(utcDatetime, 'relative', timezone)
  }
  return formatDateTime(utcDatetime, timezone)
}

export function truncate(str: string, length: number): string {
  if (!str) return ''
  return str.length > length ? str.slice(0, length) + '...' : str
}

export function getInitials(name: string): string {
  if (!name) return '?'
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

/**
 * Best-effort human-readable text out of an axios error. DRF reports field
 * errors as `{ field: ["msg"] }` (e.g. the flow name-uniqueness 400), while the
 * project's generic handler uses `message`/`error`/`detail` — check both.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data
  if (typeof data === 'string' && data.trim()) return data
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>
    for (const key of ['message', 'error', 'detail', 'name', 'non_field_errors']) {
      const value = record[key]
      if (typeof value === 'string' && value.trim()) return value
      if (Array.isArray(value) && typeof value[0] === 'string' && value[0].trim()) {
        return value[0]
      }
    }
  }
  const message = (err as { message?: unknown })?.message
  return typeof message === 'string' && message.trim() ? message : fallback
}

/**
 * Human-readable text for a form-shaped API error, listing EVERY field the
 * backend complained about (`name: ...; components: ...`) rather than just the
 * first one — a template draft can fail validation on several fields at once.
 *
 * The axios response interceptor rejects with a NORMALIZED `ApiError`
 * (flat `{ message, errors, status }`), so `err.response.data` is gone by the
 * time a mutation's `onError` runs; read the flat shape first and only then
 * fall back to a raw axios error for callers that bypass the interceptor.
 */
export function apiFieldErrorsMessage(err: unknown, fallback: string): string {
  const record = (err ?? {}) as Record<string, unknown>
  const fieldMaps: unknown[] = [
    record.errors,
    (record.response as { data?: unknown } | undefined)?.data,
  ]

  for (const raw of fieldMaps) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    const parts: string[] = []
    for (const [field, value] of Object.entries(raw as Record<string, unknown>)) {
      const first = Array.isArray(value)
        ? value.find((v) => typeof v === 'string')
        : typeof value === 'string'
          ? value
          : ''
      const text = typeof first === 'string' ? first : ''
      if (!text.trim()) continue
      // One readable sentence per field ("Name is required."), never the raw
      // "name: This field is required." — non-field keys are shown bare.
      const sentence = humanizeFieldError(field, text)
      parts.push(/[.!?]$/.test(sentence) ? sentence : `${sentence}.`)
    }
    if (parts.length) return parts.join(' ')
  }

  return apiErrorMessage(err, fallback)
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => any>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((...args: any[]) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }) as T
}
